import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  BodyText,
  Button,
  IllusStoreOpening,
  Image,
  TextLink,
  theme,
  TitleText,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { PageWrapper } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { AuthRedirectTarget } from '../../navigation/AppNavigation.props';

// ────────────────────────────────────────────────────────────────────────────────

export const SignedOutView = (props: SignedOutViewProps) => {
  const { redirectTo = 'home' } = props;

  const { t } = useLocalizationContext();

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const { startSignIn, startJoin } = useAuthFlow();
  const { navigate } = useNavigation();

  // ─── Helpers ─────────────────────────────────────────────────────

  const handlePressSignIn = useCallback(() => {
    if (shouldUseAuthFlowRework) {
      navigate('Auth', {
        screen: 'Login',
        params: { redirect: redirectTo },
      });

      return;
    }

    startSignIn(redirectTo);
  }, [shouldUseAuthFlowRework, startSignIn, redirectTo, navigate]);

  const handlePressJoin = useCallback(() => {
    if (shouldUseAuthFlowRework) {
      navigate('Auth', {
        screen: 'Join',
        params: { redirect: redirectTo },
      });

      return;
    }

    startJoin(redirectTo);
  }, [shouldUseAuthFlowRework, startJoin, redirectTo, navigate]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper safeAreaEdges={['top']}>
      <View testID="signed-out-view" style={styles.container}>
        <View style={styles.info}>
          <Image
            source={IllusStoreOpening}
            style={styles.image}
            accessibilityIgnoresInvertColors
            resizeMode="contain"
          />

          <TitleText size={5}>{t('signed-out-view.welcome')}</TitleText>
        </View>

        <Button
          accessibilityLabel={t('signed-out-view.sign-in')}
          accessibilityHint={t('signed-out-view.sign-in')}
          onPress={handlePressSignIn}
          style={styles.signInButton}
          size="large"
          palette="primary"
        >
          {t('signed-out-view.sign-in')}
        </Button>

        <View style={styles.row}>
          <BodyText size={3}>{t('signed-out-view.not-joined')}</BodyText>

          <TextLink
            accessibilityRole="link"
            accessibilityLabel={t('signed-out-view.join-now')}
            accessibilityHint={t('signed-out-view.join-now')}
            onPress={handlePressJoin}
            style={styles.linkWrapper}
          >
            {t('signed-out-view.join-now')}
          </TextLink>
        </View>
      </View>
    </PageWrapper>
  );
};

// ─── Props ───────────────────────────────────────────────────────────────────

type SignedOutViewProps = {
  readonly redirectTo?: AuthRedirectTarget;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: theme.spacing.large,
    paddingHorizontal: theme.spacing.medium,
  },
  info: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  signInButton: {
    marginVertical: theme.spacing.medium,
    marginHorizontal: 'auto',
    maxWidth: '100%',
    width: 340,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  linkWrapper: {
    marginLeft: theme.spacing.xsmall,
  },
  image: {
    height: 240,
    width: 240,
  },
});
